/**
 * @fileoverview Coupon Offers API Wrapper Module
 * 
 * This module provides functionality for validating and making API requests to the coupon offers endpoint.
 * It includes comprehensive payload validation, type checking, error handling, and response logging capabilities.
 * 
 * Key Features:
 * - Strict payload validation with mandatory field checking
 * - Type safety with TypeScript interfaces and type guards
 * - Automatic key normalization and null/empty value removal
 * - Comprehensive error handling and logging
 * - Loading state management with UI feedback
 * - Environment-based validation controls
 * - Success and failure response logging
 * 
 * @module apiCouponOffersWrapper
 * @version 1.0.0
 */

import { CouponOffersPayloadModel } from "./interface/couponsOffers/couponOffersInterface";
import { apiRequest2 } from "../../api/apiRequest";
import { startLoader, stopLoader } from "../../util/loader";
import { normalizeKeys, removeNullAndEmpty, storeFailedResponse } from "./helperModules/helperFunctions";
import { couponOffersDataKeys  } from "./keys/couponOffersKeys";
import { successLogger } from "../../api/apiTester";

/**
 * Validates a coupon offers payload against the expected schema and data types.
 * 
 * This function performs comprehensive validation including:
 * - Mandatory field presence checking (conceptID, webLocationID)
 * - Invalid key detection against allowed schema
 * - Type validation for all payload properties
 * - Automatic payload normalization and cleanup
 * 
 * The validation process:
 * 1. Normalizes all keys to ensure consistent casing
 * 2. Removes null and empty values from the payload
 * 3. Checks for presence of all mandatory fields
 * 4. Validates that no invalid keys are present
 * 5. Performs type checking against the schema definition
 * 
 * @param {any} payload - The payload object to validate
 * @returns {payload is CouponOffersPayloadModel} Type guard that returns true if payload is valid
 * 
 * @example
 * ```typescript
 * const payload = {
 *   conceptID: 123,
 *   webLocationID: 456,
 *   dbaName: "Business Name",
 *   serviceCategories: ["category1", "category2"]
 * };
 * 
 * if (isValidCouponOffersPayload(payload)) {
 *   // payload is now typed as CouponOffersPayloadModel
 *   console.log('Valid payload:', payload.conceptID);
 * } else {
 *   console.error('Invalid payload structure');
 * }
 * ```
 * 
 * @throws {Error} Logs validation errors to console but does not throw
 * @see {@link CouponOffersPayloadModel} for the expected payload structure
 * @see {@link couponOffersDataKeys} for the validation schema
 */
export function isValidCouponOffersPayload(payload: any): payload is CouponOffersPayloadModel {
    let payloadLower = JSON.parse(JSON.stringify(normalizeKeys(payload)));
        payloadLower = JSON.parse(JSON.stringify(removeNullAndEmpty(payloadLower)));

    //checking if all mandatory fields are present
    const mandatoryFields = ['conceptID', 'webLocationID'];
    const hasAllMandatoryFields = mandatoryFields.every(field => payloadLower?.hasOwnProperty(normalizeKeys(field)));
    if (!hasAllMandatoryFields) return false;

    //checking if any invalid keys are present
    const hasInvalidKeys = Object.keys(payloadLower).some((key) => {
      if(!couponOffersDataKeys.hasOwnProperty(key)){
        console.error('Invalid key in coupon offers Payload:', key);
        return true;
      }
    });
    if (hasInvalidKeys) return false;

    const hasInvalidTypes = Object.entries(payloadLower).some(([key, value]) => {
        if (value === undefined) {
          console.error('Undefined value in coupon offers Payload:', key);
          return true; // Return true to indicate this is an invalid type
        }
        console.log(typeof value, couponOffersDataKeys[key],"Value:",value,"Key:",key);
        return typeof value !== couponOffersDataKeys[key];
    });

    if (hasInvalidTypes) return false;
    return payload;
}

/**
 * Wrapper function for making POST requests to the coupon offers API endpoint.
 * 
 * This function handles the complete lifecycle of a coupon offers API request including:
 * - Optional strict payload validation based on environment configuration
 * - Payload normalization and preparation
 * - Loading state management with UI feedback
 * - API request execution with proper error handling
 * - Success and failure response logging
 * - Comprehensive error reporting and storage
 * 
 * The function supports two validation modes:
 * - Strict mode: Enabled via ENABLE_STRICT_PAYLOAD_VALIDATION environment variable
 * - Permissive mode: Allows requests with minimal validation
 * 
 * @param {string} url - The API endpoint URL for the coupon offers request
 * @param {any} payload - The request payload containing coupon offers data
 * @returns {Promise<any>} Promise that resolves to the API response data
 * 
 * @example
 * ```typescript
 * try {
 *   const response = await couponOffersWrapper(
 *     'https://api.example.com/coupon-offers',
 *     {
 *       conceptID: 123,
 *       webLocationID: 456,
 *       dbaName: "Business Name",
 *       serviceCategories: ["plumbing", "electrical"]
 *     }
 *   );
 *   console.log('Coupon offers retrieved:', response);
 * } catch (error) {
 *   console.error('Failed to get coupon offers:', error.message);
 * }
 * ```
 * 
 * @throws {Error} Throws error if strict validation fails or API request fails
 * @throws {Error} Throws error with detailed message including original error context
 * 
 * @see {@link isValidCouponOffersPayload} for payload validation logic
 * @see {@link CouponOffersPayloadModel} for expected payload structure
 * 
 * @async
 * @function
 */
export async function couponOffersWrapper(url: string, payload: any) {
  const suLogger = localStorage.getItem("suLogger") === 'true';

  const enableStrictValidation = process.env.ENABLE_STRICT_PAYLOAD_VALIDATION?.toLowerCase() === 'true';
  if (enableStrictValidation && !isValidCouponOffersPayload(payload)) {
    console.error("Invalid payload for coupon offers Endpoint:", payload);
    throw new Error(`Invalid payload: ${JSON.stringify(payload)}`);
  }
  const normalizedPayload = normalizeKeys(payload);
  const request = {
    method: 'POST',
    url,
    data: normalizedPayload,
  };

  try {
    startLoader();
    const response = await apiRequest2(request);
    successLogger(request.data, request.url, suLogger, response);
    return response;
  } catch (err: any) {
    console.error('Error in couponOffersWrapper:', err);
    storeFailedResponse(request, err);
    throw new Error(`couponOffersWrapper failed: ${err.message || err}`);
  } finally {
    stopLoader();
  }
}